<template>
    <div class="step5-categories-template-picker">
        <a class="is-inline-block mb-3" @click="emit('back')">
            <span class="icon"><i class="fas fa-arrow-left"></i></span>
            <span>{{ translate("page.category_manager.back_to_options") }}</span>
        </a>

        <div class="columns">
            <div class="column is-4">
                <div class="template-selection box has-no-glow-effect">
                    <h3 class="has-text-weight-bold mb-2">{{ translate("page.category_manager.import_template_select_template") }}</h3>

                    <div v-if="loadingTemplates" class="is-flex is-justify-content-center py-4">
                        <LoadingSpinner2 />
                    </div>

                    <SelectionList
                        v-else
                        :multi-select="true"
                        :options="templates"
                        v-model="selectedTemplates"
                        :height="'100%'"
                        :comparing-function="(a: TemplateDescription, b: TemplateDescription) => a.id === b.id"
                    >
                        <template v-slot:option="{ option }">
                            <p>{{ translate(option.name) }}</p>
                        </template>
                    </SelectionList>
                </div>
            </div>

            <div class="column is-8">
                <div class="template-preview box has-no-glow-effect" v-if="selectedTemplates.length > 0">
                    <h3 class="has-text-weight-bold mb-2">
                        {{ translate("wizard.step5.template_preview_count", false, [categories.length]) }}
                    </h3>

                    <div>
                        <CategoriesPreviewList :categories="categories" :loading="loadingPreview" />
                    </div>

                    <p class="help">
                        <span class="icon">
                            <i class="fas fa-info-circle"></i>
                        </span>
                        <span>{{ translate("wizard.step5.categories_adjustable_later") }}</span>
                    </p>
                </div>

                <div class="template-placeholder box has-no-glow-effect" v-else>
                    <div class="has-text-centered py-6">
                        <span class="icon is-large has-text-grey-light">
                            <i class="fas fa-4x fa-arrow-left"></i>
                        </span>
                        <p class="has-text-grey mt-3">{{ translate("page.category_manager.import_template_no_selection_message") }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div class="field mt-4 is-flex is-justify-content-end">
            <LoadingButton
                class="button is-primary"
                :disabled="categories.length === 0"
                :is-loading="false"
                data-testid="wizard-apply-template"
                @click="applyAndContinue"
            >
                <span>{{ translate("wizard.step5.apply_and_continue") }}</span>
                <span class="icon"><i class="fas fa-arrow-right"></i></span>
            </LoadingButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, ref, watch } from "vue";
    import LoadingButton from "../../../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../../../Common/LoadingSpinner2.vue";
    import SelectionList from "../../../Common/SelectionList.vue";
    import CategoriesPreviewList from "../../../CategoriesManager/CategoriesPreviewList.vue";
    import { translate } from "../../../Common/translations";
    import { handleError } from "../../../Common/errorHandler";
    import categoriesTemplatesApi, {
        type DetailedCategoryPreview,
        type TemplateDescription,
    } from "../../../Api/Categories/categoriesTemplates";
    import { useWizardStore } from "../wizardStore";

    const emit = defineEmits<{
        back: [];
        applied: [payload: { templateNames: string[]; categoryPaths: string[]; previewCount: number; summaryLabel: string }];
    }>();

    const store = useWizardStore();

    const templates = ref<TemplateDescription[]>([]);
    const loadingTemplates = ref(true);
    const loadingPreview = ref(false);
    const selectedTemplates = ref<TemplateDescription[]>([]);
    const categories = ref<DetailedCategoryPreview[]>([]);

    onMounted(() => {
        if (store.categoriesSetupMethod === "template" && store.categoriesTemplateNames) {
            selectedTemplates.value = store.categoriesTemplateNames.map(id => ({ id, name: id }));
        }

        return categoriesTemplatesApi
            .getAvailableTemplatesForOrganizer()
            .then(templatesFromApi => {
                templates.value = templatesFromApi;
                if (store.categoriesSetupMethod === "template" && store.categoriesTemplateNames) {
                    selectedTemplates.value = templates.value.filter(t => store.categoriesTemplateNames!.includes(t.id));
                }
            })
            .catch(handleError)
            .finally(() => {
                loadingTemplates.value = false;
            });
    });

    watch(
        selectedTemplates,
        () => {
            if (selectedTemplates.value.length === 0) {
                categories.value = [];
                return;
            }

            loadingPreview.value = true;
            return fetchDetailedPreview();
        },
        { deep: true },
    );

    function fetchDetailedPreview() {
        return categoriesTemplatesApi
            .getDetailedPreviewForOrganizer({
                templateNames: selectedTemplates.value.map(t => t.id),
            })
            .then(response => {
                categories.value = response.categories;
            })
            .catch(handleError)
            .finally(() => {
                loadingPreview.value = false;
            });
    }

    function applyAndContinue() {
        if (categories.value.length === 0) {
            return;
        }

        emit("applied", {
            templateNames: selectedTemplates.value.map(t => t.id),
            categoryPaths: categories.value.map(c => c.path),
            previewCount: categories.value.length,
            summaryLabel: selectedTemplates.value.map(t => translate(t.name)).join(", "),
        });
    }
</script>

<style scoped>
    .template-selection,
    .template-placeholder {
        min-height: 40vh;
        max-height: 40vh;
        display: flex;
        flex-direction: column;
    }

    .template-selection {
        overflow-y: auto;
    }

    .template-placeholder {
        justify-content: center;
    }
</style>
